import React, { useEffect, useState } from 'react';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/standard-button';
import QuestionIcon from '@atlaskit/icon/glyph/question';
import { defaultFilter as defaultIssueTypeScreenSchemesFilter } from '@atlassian/jira-admin-screens-services/src/services/issue-type-screen-schemes-service/actions/utils.tsx';
import { useIssueTypeScreenSchemesService } from '@atlassian/jira-admin-screens-services/src/services/issue-type-screen-schemes-service/index.tsx';
import { PageLayout, DataIdWrapper } from '@atlassian/jira-admin-screens-services/src/ui/index.tsx';
import ErrorStateOriginal from '@atlassian/jira-common-components-error-state';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { ADD_ISSUE_TYPE_SCREEN_SCHEME } from '../constants';
import { AsyncAddIssueTypeScreenSchemeModal } from './add-issue-type-screen-scheme-modal/async';
import IssueTypeScreenSchemeSearchBox from './issue-type-screen-scheme-search-box';
import IssueTypeScreenSchemesTable from './issue-type-screen-schemes-table';
import messages from './messages';
import PageDescription from './page-description';
import type { Props } from './types';
import { extractAnalyticsAttributes } from './utils';

export default function IssueTypeScreenSchemesApp(props: Props) {
	const { formatMessage } = useIntl();
	const [isAddModalOpen, setAddModalOpen] = useState(false);
	const [{ issueTypeScreenSchemes, filter }, { fetchIssueTypeScreenSchemes }] =
		useIssueTypeScreenSchemesService();

	// router props
	const { filter: filterFromRouter = {}, onFilterChange } = props;
	const { pageNumber, searchFilter, orderBy } = filterFromRouter;

	useEffect(() => {
		fetchIssueTypeScreenSchemes({
			...defaultIssueTypeScreenSchemesFilter,
			pageNumber,
			searchFilter,
			orderBy,
		});
	}, [fetchIssueTypeScreenSchemes, pageNumber, searchFilter, orderBy]);

	const pageHeaderActions = (
		<ButtonGroup>
			<Button
				appearance="primary"
				onClick={(_, analyticsEvent) => {
					setAddModalOpen(!isAddModalOpen);
					if (analyticsEvent) {
						fireUIAnalytics(analyticsEvent, ADD_ISSUE_TYPE_SCREEN_SCHEME);
					}
				}}
				testId="admin-pages-issue-type-screen-schemes-page.ui.add-issue-type-button"
			>
				{formatMessage(messages.addIssueTypeScreenScheme)}
			</Button>
			<Button
				appearance="subtle-link"
				href="https://support.atlassian.com/jira-cloud-administration/docs/manage-issue-type-screens/"
				target="_blank"
				iconBefore={<QuestionIcon label="Learn more" />}
				testId="admin-pages-issue-type-screen-schemes-page.ui.add-issue-type-help-button"
			/>
		</ButtonGroup>
	);

	const IssueTypeScreenSchemesPage = (
		<PageLayout>
			<DataIdWrapper data-test-id="admin-pages-issue-type-screen-schemes-page.ui.issue-type-screen-schemes-page">
				<UFOSegment name="issue-type-screen-schemes-page">
					<PageHeader
						actions={pageHeaderActions}
						bottomBar={
							<>
								<PageDescription />
								<br />
								<IssueTypeScreenSchemeSearchBox
									onFilterChange={(searchString) => {
										onFilterChange({
											...filter,
											pageNumber: 1,
											searchFilter: searchString,
										});
									}}
									searchFilter={searchFilter}
								/>
							</>
						}
					>
						{formatMessage(messages.pageHeader)}
					</PageHeader>
					<AsyncAddIssueTypeScreenSchemeModal
						isModalOpen={isAddModalOpen}
						setIsModalOpen={setAddModalOpen}
					/>
					{!issueTypeScreenSchemes.isError ? (
						<IssueTypeScreenSchemesTable
							total={issueTypeScreenSchemes.total}
							loading={issueTypeScreenSchemes.isLoading}
							values={issueTypeScreenSchemes.values}
							filter={filter}
							onFilterChange={onFilterChange}
						/>
					) : (
						<ErrorStateOriginal
							header={formatMessage(messages.errorHeader)}
							description={formatMessage(messages.errorDescription)}
							onRetry={() => fetchIssueTypeScreenSchemes(filter)}
						/>
					)}
				</UFOSegment>
			</DataIdWrapper>
		</PageLayout>
	);
	return (
		<ContextualAnalyticsData
			attributes={extractAnalyticsAttributes(filter, issueTypeScreenSchemes.total)}
		>
			{IssueTypeScreenSchemesPage}
		</ContextualAnalyticsData>
	);
}
